import {
  createDefaultLandingSections,
  createLandingSectionsFromPreset,
  normalizeLandingSections,
} from '../publicPage/registry'
import {
  SECTION_TEMPLATES,
  createDefaultLandingPagePreset,
  createSectionDraft,
} from '../orgHomepage/registry'
import { normalizeLandingPageData } from '../orgHomepage/normalize'
import {
  createCategoryLandingDefaultFormFields,
  getCodeTemplatePreset,
  normalizeCategoryLandingLayoutConfig,
} from '../../templates/categoryLanding/registry'

const clone = (value) => {
  if (value == null) return value
  try {
    return JSON.parse(JSON.stringify(value))
  } catch {
    return value
  }
}

const featureOn = (features, key) => features?.[key] === true

export const CARD_RECONFIGURE_PURPOSES = [
  {
    id: 'basic',
    label: '기본형',
    description: '가장 익숙한 기본 명함 레이아웃',
    templateId: 'CARD_V1',
  },
  {
    id: 'premium',
    label: '프리미엄형',
    description: '큰 프로필 블록과 고급스러운 네이비 톤',
    templateId: 'CARD_V2',
  },
  {
    id: 'clean',
    label: '클린형',
    description: '밝고 깔끔한 상담형 레이아웃',
    templateId: 'CARD_V3',
  },
  {
    id: 'centerBlue',
    label: '센터 블루',
    description: '가운데 정렬과 딥블루 배경의 심플한 스타일',
    templateId: 'CARD_V4',
  },
  {
    id: 'brandBlock',
    label: '브랜드 블록',
    description: '프로필 사진을 카드 하단 큰 영역으로 강조',
    templateId: 'CARD_V5',
  },
  {
    id: 'monoDark',
    label: '모노 다크',
    description: '블랙 배경과 고대비 타이포의 미니멀 명함',
    templateId: 'CARD_V6',
  },
  {
    id: 'forest',
    label: '포레스트',
    description: '딥그린과 크림 톤의 차분한 스타일',
    templateId: 'CARD_V7',
  },
  {
    id: 'magazine',
    label: '매거진',
    description: '상단 히어로 이미지와 겹침 카드형 레이아웃',
    templateId: 'CARD_V8',
  },
  {
    id: 'timeline',
    label: '타임라인 네이비',
    description: '서비스, 연혁, 강조 수치를 함께 보여주는 신뢰형',
    templateId: 'CARD_V9',
  },
  {
    id: 'hubLight',
    label: '허브 라이트',
    description: '사진, 프로필, 경력, 태그를 밝은 흐름으로 구성',
    templateId: 'CARD_V10',
  },
  {
    id: 'hubDark',
    label: '허브 다크',
    description: '정보 밀도 높은 허브형에 어두운 프리미엄 톤',
    templateId: 'CARD_V11',
  },
  {
    id: 'photo',
    label: '프로필사진 강조',
    description: '프로필 사진이 크게 보이는 첫인상 중심 명함',
    templateId: 'CARD_V12',
  },
]

export const CARD_RECONFIGURE_FEATURES = [
  { id: 'showLandingLink', label: '개인홈페이지 링크', description: '명함에서 개인홈페이지로 바로 이동할 수 있어요', recommended: true },
  { id: 'showCareerCard', label: '경력 카드', description: '전문성과 이력을 짧게 보여줘요', recommended: true },
  { id: 'showActivityAreaCard', label: '활동지역', description: '상담 가능한 지역을 알려줘요', recommended: true },
  { id: 'showFreeTextCard', label: '자유 소개', description: '하고 싶은 말을 조금 더 길게 보여줘요' },
]

export function getCardPurposeFromTemplate(templateId) {
  const raw = String(templateId || '').toUpperCase()
  const matched = CARD_RECONFIGURE_PURPOSES.find((item) => item.templateId === raw)
  return matched?.id || 'basic'
}

export function getDefaultCardFeatureValues(current = {}) {
  return {
    showLandingLink: current.showLandingLink !== false,
    showCareerCard: current.showCareerCard !== false,
    showActivityAreaCard: current.showActivityAreaCard !== false,
    showFreeTextCard: current.showFreeTextCard === true,
  }
}

export function buildCardReconfigureState({ purposeId = 'basic', features = {} }) {
  const purpose = CARD_RECONFIGURE_PURPOSES.find((item) => item.id === purposeId) || CARD_RECONFIGURE_PURPOSES[0]
  return {
    cardTemplateId: purpose.templateId,
    showLandingLink: featureOn(features, 'showLandingLink'),
    showCareerCard: featureOn(features, 'showCareerCard'),
    showActivityAreaCard: featureOn(features, 'showActivityAreaCard'),
    showFreeTextCard: featureOn(features, 'showFreeTextCard'),
  }
}

export const PERSONAL_LANDING_RECONFIGURE_PURPOSES = [
  { id: 'trust', label: '신뢰를 보여주기', description: '소개, 경력, 지표를 중심으로 구성해요' },
  { id: 'conversion', label: '상담 문의 받기', description: '상담 사례와 문의 버튼을 앞쪽에 배치해요' },
  { id: 'brand', label: '나만의 분위기 보여주기', description: '사진, 자유 소개, 서비스 태그를 중심으로 보여줘요' },
]

export const PERSONAL_LANDING_RECONFIGURE_FEATURES = [
  { id: 'hero', label: '상단 소개', description: '첫 화면에 기본으로 들어가요', required: true },
  { id: 'about-profile', label: '소개 페이지', description: '상담 스타일과 강점을 보여줘요', required: true },
  { id: 'service-tags', label: '서비스 태그', description: '도움 줄 수 있는 분야를 짧게 보여줘요', recommended: true },
  { id: 'proof-stats', label: '성과 지표', description: '상담 건수나 응답률처럼 숫자로 신뢰를 보여줘요' },
  { id: 'monthly-issue', label: '이달의 이슈', description: '지금 고객이 관심 가질 만한 주제를 보여줘요' },
  { id: 'media-gallery', label: '이미지 갤러리', description: '사진으로 활동과 분위기를 보여줘요' },
  { id: 'image-card', label: '이미지 카드', description: '한 장의 이미지로 메시지를 강조해요' },
  { id: 'career', label: '경력/이력', description: '전문성과 이력을 보여줘요', recommended: true },
  { id: 'customer-case', label: '상담 사례', description: '고객이 이해하기 쉬운 사례를 보여줘요' },
  { id: 'activity', label: '활동지역', description: '상담 가능한 지역을 알려줘요', recommended: true },
  { id: 'free-text', label: '자유 소개', description: '정해진 항목 외에 하고 싶은 말을 담아요' },
  { id: 'contact', label: '문의 정보', description: '주소와 상담 링크를 보여줘요', recommended: true },
  { id: 'cta', label: '상담 유도', description: '상담 신청 버튼을 강조해요', recommended: true },
  { id: 'credentials', label: '자격/이력', description: '자격과 인증 정보를 보여줘요' },
  { id: 'faq', label: '자주 묻는 질문', description: '반복 질문을 줄일 수 있어요' },
  { id: 'ad-review', label: '심의필/준법 문구', description: '보험 홍보에는 기본으로 필요해요', required: true },
]

const PERSONAL_DEFAULT_FEATURES_BY_PURPOSE = {
  trust: {
    'service-tags': true,
    'proof-stats': true,
    career: true,
    activity: true,
    contact: true,
    cta: true,
    credentials: true,
    faq: true,
  },
  conversion: {
    'service-tags': true,
    'monthly-issue': true,
    'customer-case': true,
    'proof-stats': true,
    'image-card': true,
    contact: true,
    cta: true,
    credentials: true,
    faq: true,
  },
  brand: {
    'service-tags': true,
    'media-gallery': true,
    'free-text': true,
    contact: true,
    cta: true,
    credentials: true,
  },
}

const PERSONAL_ORDER_BY_PURPOSE = {
  trust: ['hero', 'about-profile', 'service-tags', 'proof-stats', 'career', 'activity', 'contact', 'cta', 'credentials', 'faq', 'ad-review'],
  conversion: ['hero', 'about-profile', 'monthly-issue', 'customer-case', 'proof-stats', 'image-card', 'contact', 'cta', 'credentials', 'faq', 'ad-review'],
  brand: ['hero', 'about-profile', 'media-gallery', 'free-text', 'service-tags', 'contact', 'cta', 'credentials', 'faq', 'ad-review'],
}

export function getPersonalLandingPurposeFromSections(sections = []) {
  const enabledTypes = new Set(
    (sections || [])
      .filter((section) => section?.enabled !== false)
      .map((section) => section?.type)
      .filter(Boolean)
  )
  if (enabledTypes.has('monthly-issue') || enabledTypes.has('customer-case') || enabledTypes.has('image-card')) return 'conversion'
  if (enabledTypes.has('media-gallery') || enabledTypes.has('free-text')) return 'brand'
  return 'trust'
}

export function getDefaultPersonalLandingFeatureValues(sections = [], purposeId = 'trust') {
  const defaults = PERSONAL_DEFAULT_FEATURES_BY_PURPOSE[purposeId] || PERSONAL_DEFAULT_FEATURES_BY_PURPOSE.trust
  const values = {}
  for (const item of PERSONAL_LANDING_RECONFIGURE_FEATURES) {
    if (item.required) {
      values[item.id] = true
      continue
    }
    const existing = (sections || []).find((section) => section?.type === item.id)
    values[item.id] = existing ? existing.enabled !== false : defaults[item.id] === true
  }
  return values
}

export function buildPersonalLandingReconfigureSections({
  data = {},
  currentSections = [],
  purposeId = 'trust',
  features = {},
}) {
  const presetSections = createLandingSectionsFromPreset(data, purposeId)
  const fallbackSections = createDefaultLandingSections(data)
  const currentMap = new Map((currentSections || []).map((section) => [section.type, section]))
  const presetMap = new Map((presetSections || []).map((section) => [section.type, section]))
  const fallbackMap = new Map((fallbackSections || []).map((section) => [section.type, section]))

  const selectedTypes = new Set(['hero', 'about-profile', 'ad-review'])
  for (const [type, enabled] of Object.entries(features || {})) {
    if (enabled) selectedTypes.add(type)
  }

  const preferredOrder = PERSONAL_ORDER_BY_PURPOSE[purposeId] || PERSONAL_ORDER_BY_PURPOSE.trust
  const allTypes = [
    ...preferredOrder.filter((type) => selectedTypes.has(type)),
    ...Array.from(selectedTypes).filter((type) => !preferredOrder.includes(type)),
  ]

  const next = allTypes
    .map((type, index) => {
      const preset = presetMap.get(type)
      const current = currentMap.get(type)
      const fallback = fallbackMap.get(type)
      const source = preset || current || fallback
      if (!source) return null
      return {
        ...clone(source),
        id: current?.id || source.id || `${type}-${index + 1}`,
        type,
        enabled: type === 'hero' || type === 'about-profile' || type === 'ad-review' || features[type] === true,
        order: index,
        content: current?.content && typeof current.content === 'object'
          ? clone(current.content)
          : clone(source.content || {}),
        assets: current?.assets && typeof current.assets === 'object'
          ? clone(current.assets)
          : clone(source.assets || {}),
        templateCode: preset?.templateCode || current?.templateCode || fallback?.templateCode,
      }
    })
    .filter(Boolean)

  return normalizeLandingSections(next, data)
}

export const CATEGORY_LANDING_RECONFIGURE_PURPOSES = [
  { id: 'quick', label: '빠른 문의 받기', description: '짧은 문구와 상담 폼을 앞세워요', presetKey: 'clean-form' },
  { id: 'explain', label: '자세히 설명하기', description: '본문 섹션을 충분히 보여줘요', presetKey: 'hanwha-sections' },
  { id: 'cta', label: '신청을 강조하기', description: '상단에서 행동 유도를 강하게 보여줘요', presetKey: 'basic-template-4' },
  { id: 'premium', label: '프리미엄 상담형', description: '전문성과 1:1 상담 느낌을 강조해요', presetKey: 'basic-template-5' },
]

export const CATEGORY_LANDING_RECONFIGURE_FEATURES = [
  { id: 'contentSections', label: '상품/서비스 설명', description: '고객에게 필요한 내용을 본문에 보여줘요', recommended: true },
  { id: 'formEnabled', label: '상담 신청 폼', description: '이름과 연락처를 받을 수 있어요', recommended: true },
  { id: 'surveyEnabled', label: '설문 받기', description: '상담 전에 필요한 정보를 미리 받을 수 있어요' },
  { id: 'adReviewNotice', label: '심의필 문구', description: '보험 광고 페이지에는 기본으로 필요해요', required: true },
]

const CATEGORY_DEFAULT_FEATURES_BY_PURPOSE = {
  quick: { contentSections: false, formEnabled: true, surveyEnabled: false },
  explain: { contentSections: true, formEnabled: true, surveyEnabled: false },
  cta: { contentSections: true, formEnabled: true, surveyEnabled: false },
  premium: { contentSections: true, formEnabled: true, surveyEnabled: false },
}

export function getCategoryPurposeFromTemplate(templateId) {
  const raw = String(templateId || '').replace(/^preset:/, '')
  const matched = CATEGORY_LANDING_RECONFIGURE_PURPOSES.find((item) => item.presetKey === raw)
  return matched?.id || 'quick'
}

export function getDefaultCategoryLandingFeatureValues(current = {}, purposeId = 'quick', options = {}) {
  const defaults = CATEGORY_DEFAULT_FEATURES_BY_PURPOSE[purposeId] || CATEGORY_DEFAULT_FEATURES_BY_PURPOSE.quick
  const preferCurrent = options.preferCurrent !== false
  const hasCurrentSectionsValue = Array.isArray(current.contentSections)
  const hasCurrentFormValue = typeof current.formEnabled === 'boolean'
  const hasCurrentSurveyValue = typeof current.surveyEnabled === 'boolean'
  return {
    contentSections: preferCurrent && hasCurrentSectionsValue ? current.contentSections.length > 0 : defaults.contentSections === true,
    formEnabled: preferCurrent && hasCurrentFormValue ? current.formEnabled : defaults.formEnabled !== false,
    surveyEnabled: preferCurrent && hasCurrentSurveyValue ? current.surveyEnabled : defaults.surveyEnabled === true,
    adReviewNotice: true,
  }
}

export function buildCategoryLandingReconfigureState({
  category,
  purposeId = 'quick',
  features = {},
  current = {},
}) {
  const purpose = CATEGORY_LANDING_RECONFIGURE_PURPOSES.find((item) => item.id === purposeId) || CATEGORY_LANDING_RECONFIGURE_PURPOSES[0]
  const preset = getCodeTemplatePreset(category, purpose.presetKey) || getCodeTemplatePreset(category, 'default') || {}
  const contentSectionsOn = featureOn(features, 'contentSections')
  const formOn = featureOn(features, 'formEnabled')
  const surveyOn = featureOn(features, 'surveyEnabled')
  const currentSections = Array.isArray(current.contentSections) ? current.contentSections : []
  const presetSections = Array.isArray(preset.contentSections) ? preset.contentSections : []
  const currentFormFields = Array.isArray(current.formFields) ? current.formFields : []
  const currentSurveyFields = Array.isArray(current.surveyCustomFields) ? current.surveyCustomFields : []

  const presetConfig = normalizeCategoryLandingLayoutConfig(preset.customConfig || {})
  const currentConfig = normalizeCategoryLandingLayoutConfig(current.customConfig || {})
  const templateId = purpose.presetKey === 'default' ? 'default' : `preset:${purpose.presetKey}`

  return {
    templateId,
    heroTitle: String(current.heroTitle || '').trim() || preset.heroTitle || '',
    heroSubtitle: String(current.heroSubtitle || '').trim() || preset.heroSubtitle || '',
    heroImageFileId: String(current.heroImageFileId || '').trim() || preset.heroImageFileId || '',
    heroVideoFileId: current.heroVideoFileId || '',
    formTitle: String(current.formTitle || '').trim() || preset.formTitle || '상담 신청',
    contentSections: contentSectionsOn ? clone(presetSections.length > 0 ? presetSections : currentSections) : [],
    formEnabled: formOn,
    formFields: formOn
      ? clone(currentFormFields.length > 0 ? currentFormFields : (preset.formFields || createCategoryLandingDefaultFormFields({ includeMessage: true })))
      : [],
    surveyEnabled: surveyOn,
    surveyCustomFields: surveyOn
      ? clone(currentSurveyFields.length > 0 ? currentSurveyFields : (preset.surveyCustomFields || []))
      : [],
    customConfig: normalizeCategoryLandingLayoutConfig({
      ...currentConfig,
      ...presetConfig,
    }),
  }
}

export const ORG_HOMEPAGE_RECONFIGURE_PURPOSES = [
  { id: 'recruit', label: '채용 문의 받기', description: '입사 혜택과 문의 폼을 앞쪽에 배치해요' },
  { id: 'intro', label: '조직 소개하기', description: '조직 소개와 대표 메시지를 중심으로 보여줘요' },
  { id: 'service', label: '서비스 안내하기', description: '주요 서비스와 문의 정보를 보기 쉽게 구성해요' },
]

export const ORG_HOMEPAGE_RECONFIGURE_FEATURES = [
  { id: 'hero', label: '상단 소개', description: '첫 화면에 기본으로 들어가요', required: true },
  { id: 'ga-intro', label: '조직 소개', description: '조직의 방향과 문화를 보여줘요', recommended: true },
  { id: 'ceo-message', label: '대표 메시지', description: '리더의 메시지로 신뢰를 줘요' },
  { id: 'business-intro', label: '사업단 소개', description: '팀이나 사업 구조를 보여줘요' },
  { id: 'benefits', label: '입사 혜택', description: '함께 일할 때의 장점을 보여줘요' },
  { id: 'stats', label: '성과 지표', description: '조직 규모나 성과를 숫자로 보여줘요' },
  { id: 'lead-form', label: '입사/문의 폼', description: '관심 있는 사람의 연락처를 받을 수 있어요', recommended: true },
  { id: 'contact', label: '연락처', description: '조직 연락처와 문의 정보를 보여줘요', recommended: true },
]

const ORG_DEFAULT_FEATURES_BY_PURPOSE = {
  recruit: {
    'ga-intro': true,
    benefits: true,
    stats: true,
    'lead-form': true,
    contact: true,
  },
  intro: {
    'ga-intro': true,
    'ceo-message': true,
    'business-intro': true,
    stats: true,
    contact: true,
  },
  service: {
    'ga-intro': true,
    'business-intro': true,
    stats: true,
    contact: true,
  },
}

const ORG_ORDER_BY_PURPOSE = {
  recruit: ['hero', 'ga-intro', 'benefits', 'stats', 'business-intro', 'lead-form', 'contact'],
  intro: ['hero', 'ga-intro', 'ceo-message', 'business-intro', 'stats', 'contact'],
  service: ['hero', 'ga-intro', 'business-intro', 'stats', 'contact'],
}

export function getOrgHomepagePurposeFromState(pageState = {}) {
  const enabledTypes = new Set(
    (Array.isArray(pageState.sections) ? pageState.sections : [])
      .filter((section) => section?.enabled !== false)
      .map((section) => section?.type)
      .filter(Boolean)
  )
  if (enabledTypes.has('benefits') || enabledTypes.has('lead-form')) return 'recruit'
  if (enabledTypes.has('ceo-message')) return 'intro'
  if (enabledTypes.has('business-intro')) return 'service'
  return 'recruit'
}

export function getDefaultOrgHomepageFeatureValues(pageState = {}, purposeId = 'recruit') {
  const defaults = ORG_DEFAULT_FEATURES_BY_PURPOSE[purposeId] || ORG_DEFAULT_FEATURES_BY_PURPOSE.recruit
  const sections = Array.isArray(pageState.sections) ? pageState.sections : []
  const values = {}
  for (const item of ORG_HOMEPAGE_RECONFIGURE_FEATURES) {
    if (item.required) {
      values[item.id] = true
      continue
    }
    const existing = sections.find((section) => section?.type === item.id)
    values[item.id] = existing ? existing.enabled !== false : defaults[item.id] === true
  }
  return values
}

export function buildOrgHomepageReconfigureState({
  currentPageState = {},
  purposeId = 'recruit',
  features = {},
}) {
  const current = normalizeLandingPageData(currentPageState || createDefaultLandingPagePreset())
  const fallback = normalizeLandingPageData(createDefaultLandingPagePreset())
  const currentMap = new Map((current.sections || []).map((section) => [section.type, section]))
  const fallbackMap = new Map((fallback.sections || []).map((section) => [section.type, section]))
  const metaByType = new Map(SECTION_TEMPLATES.map((item) => [item.type, item]))

  const selectedTypes = new Set(['hero'])
  for (const [type, enabled] of Object.entries(features || {})) {
    if (enabled) selectedTypes.add(type)
  }
  const order = ORG_ORDER_BY_PURPOSE[purposeId] || ORG_ORDER_BY_PURPOSE.recruit
  const allTypes = [
    ...order.filter((type) => selectedTypes.has(type)),
    ...Array.from(selectedTypes).filter((type) => !order.includes(type)),
  ]

  const sections = allTypes
    .map((type, index) => {
      const meta = metaByType.get(type)
      const source = currentMap.get(type) || fallbackMap.get(type) || createSectionDraft(type, meta?.templates?.[0]?.code)
      if (!source) return null
      return {
        ...clone(source),
        type,
        enabled: type === 'hero' || features[type] === true,
        order: index,
      }
    })
    .filter(Boolean)

  return normalizeLandingPageData({
    ...current,
    sections,
    navigationMenu: sections
      .filter((section) => section.enabled !== false)
      .map((section) => ({
        sectionId: section.id,
        label: section?.content?.title || metaByType.get(section.type)?.label || section.type,
      })),
  })
}
